import { Avatar, AvatarInitials } from "@/components/Avatar";
import { GroupSelector } from "@/components/share/GroupSelector";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading4 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import type { ShareableResource } from "@/contexts/ShareResourceContext";
import {
    useBatchUpdateRoleAssignments,
    useListGroups,
    useListRoleAssignments,
    type BatchRoleAssignmentOperation,
    type PlainGroup,
    type PlainRoleAssignment,
} from "@/queries/group-queries";
import { formatError } from "@/utils/errors";
import { getOrgRoleLabel } from "@/utils/org-roles";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { ResourceRole } from "gitpod-next-api/gitpod/v1/group_pb";
import { Trash2 } from "lucide-react";
import { useCallback, useMemo, type FC } from "react";

// Helper to get role label and description for project roles
const getProjectRoleInfo = (role: ResourceRole): { label: string; description: string } => {
    switch (role) {
        case ResourceRole.PROJECT_ADMIN:
            return {
                label: "Admin",
                description: "Full Project control including sharing and management.",
            };
        case ResourceRole.PROJECT_EDITOR:
            return {
                label: "Editor",
                description: "Can update, delete, and manage Environment classes and Secrets.",
            };
        case ResourceRole.PROJECT_USER:
            return {
                label: "User",
                description: "Basic Project access for viewing and using.",
            };
        default:
            return {
                label: "User",
                description: "Basic Project access for viewing and using.",
            };
    }
};

// Helper to get role label and description for runner roles
const getRunnerRoleInfo = (role: ResourceRole): { label: string; description: string } => {
    switch (role) {
        case ResourceRole.RUNNER_ADMIN:
            return {
                label: "Admin",
                description: "Full Runner control including configuration and management.",
            };
        case ResourceRole.RUNNER_USER:
            return {
                label: "User",
                description: "Can use the runner to create and manage environments.",
            };
        default:
            return {
                label: "User",
                description: "Can use the runner to create and manage environments.",
            };
    }
};

// Helper to get role label and description for workflow roles
const getWorkflowRoleInfo = (role: ResourceRole): { label: string; description: string } => {
    switch (role) {
        case ResourceRole.WORKFLOW_ADMIN:
            return {
                label: "Admin",
                description: "Full automation control including sharing and management.",
            };
        case ResourceRole.WORKFLOW_EXECUTOR:
            return {
                label: "Executor",
                description: "Can view and run this automation.",
            };
        case ResourceRole.WORKFLOW_VIEWER:
            return {
                label: "Viewer",
                description: "Can view the automation but not run or modify it.",
            };
        default:
            return {
                label: "Viewer",
                description: "Can view the automation but not run or modify it.",
            };
    }
};

// Groups only have a single admin role for now. Expand this switch when more roles are added (e.g., editor).
const getGroupRoleInfo = (_role: ResourceRole): { label: string; description: string } => ({
    label: "Admin",
    description: "Can rename, share, and manage group members.",
});

// Helper to get available roles for a resource type
const getAvailableRoles = (resourceType: ResourceType): ResourceRole[] => {
    switch (resourceType) {
        case ResourceType.PROJECT:
            return [ResourceRole.PROJECT_USER, ResourceRole.PROJECT_EDITOR, ResourceRole.PROJECT_ADMIN];
        case ResourceType.RUNNER:
            return [ResourceRole.RUNNER_USER, ResourceRole.RUNNER_ADMIN];
        case ResourceType.WORKFLOW:
            return [ResourceRole.WORKFLOW_VIEWER, ResourceRole.WORKFLOW_EXECUTOR, ResourceRole.WORKFLOW_ADMIN];
        case ResourceType.GROUP:
            return [ResourceRole.GROUP_ADMIN];
        default:
            return [];
    }
};

// Helper to get role info based on resource type
const getRoleInfo = (resourceType: ResourceType, role: ResourceRole): { label: string; description: string } => {
    switch (resourceType) {
        case ResourceType.PROJECT:
            return getProjectRoleInfo(role);
        case ResourceType.RUNNER:
            return getRunnerRoleInfo(role);
        case ResourceType.WORKFLOW:
            return getWorkflowRoleInfo(role);
        case ResourceType.GROUP:
            return getGroupRoleInfo(role);
        default:
            return { label: role.toString(), description: "" };
    }
};

type Props = {
    resource: ShareableResource;
    onGroupsAdded?: (groupIds: string[]) => void;
    onGroupRemoved?: (groupId: string) => void;
};

export const GroupsWithAccessSection: FC<Props> = ({
    resource: { resourceType, resourceId },
    onGroupsAdded,
    onGroupRemoved,
}) => {
    const { toast } = useToast();
    const { data: groupsData } = useListGroups();
    const { data: assignmentsData } = useListRoleAssignments({ resourceType, resourceId });
    const batchUpdate = useBatchUpdateRoleAssignments();

    const availableRoles = useMemo(() => getAvailableRoles(resourceType), [resourceType]);
    const defaultRole = availableRoles[0] || ResourceRole.UNSPECIFIED;

    // Filter out system-managed groups (including org-members) and direct share groups.
    // System-managed groups are handled separately in GeneralAccessSection.
    // Direct share groups are internal implementation details for user-level sharing.
    const availableGroups = useMemo(() => {
        if (!groupsData?.groups) return [];
        return groupsData.groups.filter((g) => !g.systemManaged && !g.directShare);
    }, [groupsData]);

    // Get role assignments for this resource
    const roleAssignments = useMemo(() => {
        if (!assignmentsData?.assignments) return [];
        return assignmentsData.assignments;
    }, [assignmentsData]);

    // Get groups that already have role assignments
    const assignedGroupIds = useMemo(() => {
        const ids = new Set(roleAssignments.map((a) => a.groupId));
        return ids;
    }, [roleAssignments]);

    // Get groups that have org-derived assignments (should not be selectable for direct shares)
    const groupsWithOrgDerivedAssignment = useMemo(() => {
        const ids = new Set<string>();
        roleAssignments.forEach((a) => {
            if (a.derivedFromOrgRole) {
                ids.add(a.groupId);
            }
        });
        return ids;
    }, [roleAssignments]);

    // Get groups available for selection (not already assigned and not org-derived)
    const selectableGroups = useMemo(
        () =>
            availableGroups.filter(
                (g) => !assignedGroupIds.has(g.id) && !groupsWithOrgDerivedAssignment.has(g.id),
            ),
        [availableGroups, assignedGroupIds, groupsWithOrgDerivedAssignment],
    );

    const handleAddGroups = useCallback(
        async (groupIds: string[]) => {
            try {
                const operations: BatchRoleAssignmentOperation[] = groupIds.map((groupId) => ({
                    type: "create" as const,
                    groupId,
                    role: defaultRole,
                }));

                await batchUpdate.mutateAsync({
                    resourceType,
                    resourceId,
                    operations,
                });

                toast({
                    title: "Group access updated",
                });

                // Notify parent about the change
                onGroupsAdded?.(groupIds);
            } catch (error) {
                toast({
                    title: "Failed to update group access",
                    description: formatError(error),
                });
            }
        },
        [defaultRole, resourceType, resourceId, batchUpdate, toast, onGroupsAdded],
    );

    const handleRoleChange = useCallback(
        async (groupId: string, value: string) => {
            try {
                const existing = roleAssignments.find((a) => a.groupId === groupId);
                if (!existing) return;

                // Handle deletion
                if (value === "delete") {
                    const operations: BatchRoleAssignmentOperation[] = [
                        {
                            type: "delete",
                            assignmentId: existing.id,
                        },
                    ];

                    await batchUpdate.mutateAsync({
                        resourceType,
                        resourceId,
                        operations,
                    });

                    toast({
                        title: "Group access removed",
                    });

                    // Notify parent about the removal
                    onGroupRemoved?.(groupId);
                    return;
                }

                // Handle role update
                const role = parseInt(value) as ResourceRole;
                const operations: BatchRoleAssignmentOperation[] = [
                    {
                        type: "update",
                        assignmentId: existing.id,
                        groupId,
                        role,
                    },
                ];

                await batchUpdate.mutateAsync({
                    resourceType,
                    resourceId,
                    operations,
                });

                toast({
                    title: "Group access updated",
                });
            } catch (error) {
                toast({
                    title: "Failed to update group access",
                    description: formatError(error),
                });
            }
        },
        [roleAssignments, resourceType, resourceId, batchUpdate, toast, onGroupRemoved],
    );

    // Get display list of groups with their roles
    // Sorted: org-derived first, then direct shares, alphabetically within each group
    const displayGroups = useMemo(() => {
        const groups: {
            group: PlainGroup;
            role: ResourceRole;
            assignment?: PlainRoleAssignment;
            derivedFromOrgRole?: ResourceRole;
        }[] = [];

        roleAssignments.forEach((assignment) => {
            const group = availableGroups.find((g) => g.id === assignment.groupId);
            if (!group) return;

            groups.push({
                group,
                role: assignment.resourceRole,
                assignment,
                derivedFromOrgRole: assignment.derivedFromOrgRole,
            });
        });

        // Sort: org-derived first, then alphabetically by group name
        return groups.sort((a, b) => {
            // Org-derived assignments come first
            const aIsOrgDerived = !!a.derivedFromOrgRole;
            const bIsOrgDerived = !!b.derivedFromOrgRole;
            if (aIsOrgDerived !== bIsOrgDerived) {
                return aIsOrgDerived ? -1 : 1;
            }
            // Within same category, sort alphabetically
            return a.group.name.localeCompare(b.group.name);
        });
    }, [roleAssignments, availableGroups]);

    return (
        <div className="flex flex-col gap-2">
            <Heading4 className="font-medium">Groups with access</Heading4>

            <GroupSelector groups={selectableGroups} loading={!groupsData} onAdd={handleAddGroups} />

            {displayGroups.length === 0 ? (
                <div className="flex items-center justify-center rounded-lg border border-border-light bg-surface-02 px-6 py-8">
                    <Text className="text-sm text-content-secondary">No groups have access</Text>
                </div>
            ) : (
                <div className="flex flex-col gap-1">
                    {displayGroups.map(({ group, role, derivedFromOrgRole }) => {
                        const isOrgDerived = !!derivedFromOrgRole;
                        const orgRoleLabel = derivedFromOrgRole
                            ? getOrgRoleLabel(derivedFromOrgRole)
                            : undefined;
                        return (
                            <div
                                key={`${group.id}-${derivedFromOrgRole || "direct"}`}
                                className="flex items-center gap-2 px-0 py-1"
                                data-testid={`group-assignment-${group.id}`}
                            >
                                <Avatar size={32} className="size-8 rounded-md">
                                    <AvatarInitials name={group.name} size={32} />
                                </Avatar>
                                <div className="flex min-w-0 flex-1 flex-col">
                                    <div className="flex items-center gap-2">
                                        <Text className="truncate text-base">{group.name}</Text>
                                        {isOrgDerived && orgRoleLabel && (
                                            <span className="rounded border border-border-base px-1.5 py-0.5 text-xs text-content-secondary">
                                                {orgRoleLabel}
                                            </span>
                                        )}
                                    </div>
                                    <Text className="text-sm text-content-tertiary">
                                        {group.memberCount}{" "}
                                        <span>{group.memberCount === 1 ? "member" : "members"}</span>
                                    </Text>
                                </div>
                                <Select
                                    value={role.toString()}
                                    onValueChange={(value) => handleRoleChange(group.id, value)}
                                    disabled={isOrgDerived}
                                >
                                    <SelectTrigger
                                        className="w-32"
                                        loading={false}
                                        data-testid={`role-select-${group.id}`}
                                        title={isOrgDerived ? "Granted by organization role" : undefined}
                                    >
                                        <SelectValue>
                                            <span className="text-base">
                                                {getRoleInfo(resourceType, role).label}
                                            </span>
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent className="w-fit" align="end">
                                        {availableRoles.map((r) => {
                                            const roleInfo = getRoleInfo(resourceType, r);
                                            return (
                                                <SelectItem key={r} value={r.toString()}>
                                                    <div className="mr-6 flex flex-col gap-0.5">
                                                        <span className="text-base">{roleInfo.label}</span>
                                                        <span className="truncate text-sm text-content-secondary">
                                                            {roleInfo.description}
                                                        </span>
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                        <SelectSeparator />
                                        <SelectItem value="delete">
                                            <div className="flex items-center gap-2 text-content-destructive">
                                                <Trash2 className="size-4" />
                                                <span className="text-base">Remove access</span>
                                            </div>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
